/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* ROSAVIENA: lightweight local wishlist (no account required) */
(function () {
  const KEY = 'rosaviena:wishlist';
  const getIds = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]').map(String); } catch (e) { return []; }
  };
  const setIds = (ids) => localStorage.setItem(KEY, JSON.stringify([...new Set(ids.map(String))]));
  const sync = (root) => {
    const ids = getIds();
    (root || document).querySelectorAll('[data-rv-wishlist]').forEach((button) => {
      const active = ids.includes(String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = button.querySelector('.rv-wishlist__label');
      if (label) label.textContent = active ? 'Saved' : 'Save to wishlist';
    });
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rv-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const id = String(button.dataset.productId);
    let ids = getIds();
    ids = ids.includes(id) ? ids.filter((x) => x !== id) : ids.concat(id);
    setIds(ids);
    sync(document);
  });
  document.addEventListener('DOMContentLoaded', () => sync(document));
  document.addEventListener('shopify:section:load', (event) => sync(event.target));
})();

/* ROSAVIENA: wishlist handle index + wishlist page renderer */
(function () {
  const MAP_KEY = 'rosaviena:wishlist:handles';
  const readMap = () => { try { return JSON.parse(localStorage.getItem(MAP_KEY) || '{}'); } catch (e) { return {}; } };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rv-wishlist]');
    if (!button) return;
    const map = readMap();
    const id = String(button.dataset.productId || '');
    const handle = button.dataset.productHandle || '';
    setTimeout(() => {
      const active = button.getAttribute('aria-pressed') === 'true';
      if (active && handle) map[id] = handle; else delete map[id];
      localStorage.setItem(MAP_KEY, JSON.stringify(map));
    }, 0);
  });

  async function renderWishlist(root) {
    const container = root.querySelector('[data-rv-wishlist-grid]');
    if (!container) return;
    const map = readMap();
    const handles = Object.values(map);
    const empty = root.querySelector('[data-rv-wishlist-empty]');
    if (!handles.length) { if (empty) empty.hidden = false; return; }
    if (empty) empty.hidden = true;
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const money = (cents) => new Intl.NumberFormat(undefined, {style:'currency',currency}).format((Number(cents)||0)/100);
    const items = await Promise.all(handles.map(async (handle) => {
      try { const r = await fetch('/products/' + encodeURIComponent(handle) + '.js'); return r.ok ? await r.json() : null; } catch(e) { return null; }
    }));
    container.innerHTML = items.filter(Boolean).map((p) => {
      const image = p.featured_image ? `<img loading="lazy" src="${p.featured_image}" alt="${(p.title||'').replace(/"/g,'&quot;')}">` : '';
      return `<article class="rv-wishlist-page__card"><a class="rv-wishlist-page__media" href="${p.url}">${image}</a><div class="rv-wishlist-page__info"><a href="${p.url}" class="rv-wishlist-page__title">${p.title}</a><div class="rv-wishlist-page__price">${money(p.price)}</div><a class="rv-wishlist-page__link" href="${p.url}">View piece</a></div></article>`;
    }).join('');
  }
  document.addEventListener('DOMContentLoaded', () => renderWishlist(document));
  document.addEventListener('shopify:section:load', (event) => renderWishlist(event.target));
})();

/* ROSAVIENA: reusable countdown card */
(function () {
  class RosavienaCountdown extends HTMLElement {
    connectedCallback() {
      this.hoursEl = this.querySelector('[data-rv-hours]');
      this.minutesEl = this.querySelector('[data-rv-minutes]');
      this.secondsEl = this.querySelector('[data-rv-seconds]');
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }
    disconnectedCallback() { if (this.timer) clearInterval(this.timer); }
    getTarget(now) {
      const mode = this.dataset.mode || 'daily_cutoff';
      if (mode === 'fixed' && this.dataset.fixedDatetime) {
        const d = new Date(this.dataset.fixedDatetime);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const hour = Number(this.dataset.cutoffHour || 14);
      const minute = Number(this.dataset.cutoffMinute || 0);
      const target = new Date(now);
      target.setHours(hour, minute, 0, 0);
      if (target <= now) target.setDate(target.getDate() + 1);
      return target;
    }
    tick() {
      const now = new Date();
      const target = this.getTarget(now);
      if (!target) return;
      let ms = target - now;
      if (ms <= 0) {
        if ((this.dataset.mode || 'daily_cutoff') === 'fixed') {
          this.innerHTML = `<span class="rv-countdown-card__expired">${this.dataset.expiredText || 'Offer window closed'}</span>`;
          if (this.timer) clearInterval(this.timer);
          return;
        }
        ms = 0;
      }
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }
  if (!customElements.get('rv-countdown')) customElements.define('rv-countdown', RosavienaCountdown);
})();

/* ROSAVIENA: configurable stock meter */
(function () {
  class RosavienaStockMeter extends HTMLElement {
    connectedCallback() {
      try { this.inventory = JSON.parse(this.querySelector('[data-rv-stock-data]')?.textContent || '{}'); } catch(e) { this.inventory = {}; }
      this.label = this.querySelector('[data-rv-stock-label]');
      this.fill = this.querySelector('[data-rv-stock-fill]');
      this.subtext = this.querySelector('[data-rv-stock-subtext]');
      this.mode = this.dataset.mode || 'real';
      if (this.mode === 'manual') {
        this.renderManual();
        return;
      }
      const form = document.getElementById(this.getAttribute('form-id'));
      if (form) {
        form.addEventListener('variant:changed', (event) => this.render(event.detail.variant && event.detail.variant.id));
        const current = form.querySelector('[name="id"]');
        if (current) this.render(current.value);
      }
    }
    renderManual() {
      const count = Math.max(0, Number(this.dataset.manualCount || 0));
      const percent = Math.min(100, Math.max(0, Number(this.dataset.manualPercent || 0)));
      const prefix = this.dataset.manualPrefix || 'Only';
      const suffix = this.dataset.manualSuffix || 'left';
      if (this.label) this.label.textContent = `${prefix} ${count} ${suffix}`.replace(/\s+/g,' ').trim();
      if (this.fill) this.fill.style.width = `${percent}%`;
      if (this.subtext) this.subtext.textContent = this.dataset.manualSubtext || '';
      this.dataset.state = count <= 5 ? 'low' : 'normal';
      this.hidden = false;
    }
    render(id) {
      const info = this.inventory[String(id)] || {};
      const tracked = !!info.tracked;
      const available = !!info.available;
      const quantity = Number(info.quantity || 0);
      const low = Number(this.dataset.lowThreshold || 8);
      const cap = Math.max(1, Number(this.dataset.cap || 25));
      const showQty = this.dataset.showQuantity === 'true';
      if (!tracked && this.hasAttribute('data-hide-untracked')) { this.hidden = true; return; }
      this.hidden = false;
      let text = this.dataset.untrackedText || 'Available';
      let state = 'normal';
      if (tracked) {
        if (!available && quantity <= 0) { text = 'Out of stock'; state = 'out'; }
        else if (quantity > 0 && quantity <= low) { text = showQty ? `Only ${quantity} left` : 'Low stock'; state = 'low'; }
        else if (quantity > 0) { text = showQty ? `${quantity} available` : 'In stock'; state = 'normal'; }
        else { text = available ? 'Available for order' : 'Out of stock'; state = available ? 'normal' : 'out'; }
      }
      if (this.label) this.label.textContent = text;
      if (this.subtext) this.subtext.textContent = '';
      this.dataset.state = state;
      if (this.fill) {
        const progress = tracked && quantity > 0 ? Math.min(100, Math.max(8, (quantity / cap) * 100)) : (available ? 100 : 0);
        this.fill.style.width = `${progress}%`;
      }
    }
  }
  if (!customElements.get('rv-stock-meter')) customElements.define('rv-stock-meter', RosavienaStockMeter);
})();


/* ROSAVIENA: cart drawer urgency timer */
(function () {
  class RosavienaCartHoldTimer extends HTMLElement {
    connectedCallback() {
      this.timeEl = this.querySelector('[data-rv-cart-hold-time]');
      this.duration = Math.max(1, Number(this.dataset.duration || 165));
      this.storageKey = this.dataset.storageKey || 'rv-cart-urgency';
      this.tick = this.tick.bind(this);

      let expiry = Number(sessionStorage.getItem(this.storageKey) || 0);
      const now = Date.now();

      if (!expiry || expiry <= now) {
        expiry = now + (this.duration * 1000);
        try { sessionStorage.setItem(this.storageKey, String(expiry)); } catch(e) {}
      }

      this.expiry = expiry;
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.max(0, Math.ceil((this.expiry - Date.now()) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;

      if (this.timeEl) {
        this.timeEl.textContent = `${minutes}:${String(seconds).padStart(2,'0')}`;
      }

      if (remaining <= 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  if (!customElements.get('rv-cart-hold-timer')) {
    customElements.define('rv-cart-hold-timer', RosavienaCartHoldTimer);
  }

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && cart.item_count === 0) {
      try {
        Object.keys(sessionStorage).forEach((key) => {
          if (key.indexOf('rv-cart-urgency-') === 0) sessionStorage.removeItem(key);
        });
      } catch(e) {}
    }
  });
})();
